import { useTranslation } from 'react-i18next';
import { colors, screen } from 'components/styles/variables';
import Spacer from 'components/Shared/Spacer';
import { isValidVin } from 'lib/Utilities';
import css from 'styled-jsx/css';
import * as _ from 'lodash-es';
import { ReactNode } from 'react';
import PriceBubble from './PriceBubble';
import priceAnalysisStyles from './styles';

interface PriceAnalysisProps {
  vin: string;
  relativePriceDiff: number;
  price: string | null;
}

interface DifferenceMetrics {
  styles: string;
  aboveCopy: ReactNode;
  belowCopy: ReactNode;
}

const redCSS = css.resolve`
  :global(.red) {
    color: ${colors.ALR1};
  }
`;

const greenCSS = css.resolve`
  :global(.green) {
    color: ${colors.ALG2};
  }
`;

const PriceAnalysis = ({ vin, relativePriceDiff, price }: PriceAnalysisProps): JSX.Element => {
  const { t } = useTranslation();
  const validVin = isValidVin(vin);

  function calculateDifferenceMetrics(): DifferenceMetrics {
    if (!relativePriceDiff || relativePriceDiff === 0) {
      return {
        styles: 'left: 50%; width: auto; transform: translateX(-50%);',
        aboveCopy: <>{t('vdp.priceAnalysisInfo.pricedAboveSimilarVehicles')}</>,
        belowCopy: <>{t('vdp.priceAnalysisInfo.pricedBelowSimilarVehicles')}</>,
      };
    }
    if (relativePriceDiff > 0) {
      return {
        styles: 'left: 100%; width: auto; transform: translateX(-100%);',
        aboveCopy: (
          <>
            {t('vdp.priceAnalysisInfo.priced')}{' '}
            <span className={`red ${redCSS.className}`}>
              {`${relativePriceDiff}%`} {t('vdp.priceAnalysisInfo.above')}
            </span>{' '}
            {t('vdp.priceAnalysisInfo.similarVehicles')}
          </>
        ),
        belowCopy: <>{t('vdp.priceAnalysisInfo.pricedBelowSimilarVehicles')}</>,
      };
    }
    return {
      styles: 'left: 0%; width: auto; transform: translateX(-0%);',
      aboveCopy: <>{t('vdp.priceAnalysisInfo.pricedAboveSimilarVehicles')}</>,
      belowCopy: (
        <>
          {t('vdp.priceAnalysisInfo.priced')}{' '}
          <span className={`green ${greenCSS.className}`}>
            {`${Math.abs(relativePriceDiff)}%`} {t('vdp.priceAnalysisInfo.below')}
          </span>{' '}
          {t('vdp.priceAnalysisInfo.similarVehicles')}
        </>
      ),
    };
  }

  const { styles, aboveCopy, belowCopy } = calculateDifferenceMetrics();
  return (
    <div className="price-analysis">
      <div className="flex-container">
        <h2 className="price-analysis-subtitle">{t('vdp.priceAnalysis')}</h2>
        {validVin && (
          <a
            href={`https://www.carfax.com/VehicleHistory/p/Report.cfx?partner=VBR_0&vin=${vin}`}
            rel="nofollow"
            target="_blank"
            className="carfax"
          >
            <div className="text">{t('vdp.getFree')}</div>
            <div className="carfax-logo" />
          </a>
        )}
      </div>
      {!_.isNil(relativePriceDiff) ? <Spacer size="76px" smSize="95px" /> : <></>}
      <div className="market-price">
        {!_.isNil(relativePriceDiff) ? (
          <div className="listing-marker">
            <PriceBubble price={price} />
            <div className="dot" />
          </div>
        ) : (
          <div className="not-enough-data">{t('vdp.notEnoughData')}</div>
        )}
        <div className="market-price-bar" />
      </div>
      <Spacer size="16px" />
      <div className="market-price-text">
        <div className="item below-market">
          <div className="title">{t('vdp.priceAnalysisInfo.belowMarket')}</div>
          <div className="sub-title">{belowCopy}</div>
        </div>
        <div className="item market-price">
          <div className="title">{t('vdp.priceAnalysisInfo.marketPrice')}</div>
          <div className="sub-title">{t('vdp.priceAnalysisInfo.pricedAroundSimilarVehicles')}</div>
        </div>
        <div className="item above-market">
          <div className="title">{t('vdp.priceAnalysisInfo.aboveMarket')}</div>
          <div className="sub-title">{aboveCopy}</div>
        </div>
      </div>
      {validVin && (
        <a
          href={`https://www.carfax.com/VehicleHistory/p/Report.cfx?partner=VBR_0&vin=${vin}`}
          rel="nofollow"
          target="_blank"
          aria-label={t('accessibility.newWindow')}
          className="carfax-mobile"
        >
          <div className="text">{t('vdp.getFree')}</div>
          <div className="carfax-logo" />
        </a>
      )}

      <style jsx>{`
        .listing-marker {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          bottom: 1px;
          ${styles}
        }

        @media (min-width: ${screen.smMin}) {
          .listing-marker {
            bottom: 2px;
          }
        }
      `}</style>
      {redCSS.styles}
      {greenCSS.styles}
      <style jsx>{priceAnalysisStyles}</style>
    </div>
  );
};

export default PriceAnalysis;
